/**
 * ``PanelHeaderActions`` — the dockview **right-header-actions** component
 * for the workspace. Dockview renders one right-header-actions component
 * per group; this hosts the per-group header controls:
 *
 *   ``[ sort gear — platforms/detections/alerts only ][ maximize/restore — every group ]``
 *
 * The maximize toggle (:func:`MaximizeToggle`) follows the window-control
 * convention (maximize/close sit at the far right). The Sort-by gear
 * (:func:`ListSortMenu`; REQ-UI-110) sits to its LEFT and renders **only** when
 * the group's active panel is one of the three operator list panels
 * (Platforms / Detections / Alerts) — realising the per-panel preferences gear
 * (item 5) that ``chrome-spec.md`` §Panel header describes. Other panels
 * (Map, Entity Details, Audit Log, …) show the maximize toggle alone.
 *
 * # Reacting to the active panel
 *
 * A dockview group is a tab strip; ``props.group.activePanel`` is the
 * currently-shown tab and can change while this component stays mounted (the
 * operator switches tabs within the group). We track it via the group api's
 * ``onDidActivePanelChange`` + a re-read of ``group.activePanel`` so the gear
 * appears / disappears (and targets the right panel) as the active tab changes —
 * the same reactive pattern :func:`MaximizeToggle` uses for the maximized state.
 *
 * The composite row is retained (rather than wiring :func:`MaximizeToggle`
 * straight into the ``rightHeaderActionsComponent`` slot) so per-group controls
 * like the sort gear compose here without re-plumbing dockview's single-slot
 * contract.
 *
 * Cross-references:
 *   - REQ-UI-059 (maximize/restore visual affordance).
 *   - REQ-UI-110 (per-panel sort gear).
 *   - multi-pane/maximize-toggle.tsx; multi-pane/list-sort-menu.tsx.
 *   - ADR-024 §8 (panel-header affordances);
 *     docs/engineering/detail/storyboards/shared/chrome-spec.md §Panel header.
 */
import { useEffect, useState } from "react";
import type { IDockviewHeaderActionsProps } from "dockview-react";
import { MaximizeToggle } from "@/multi-pane/maximize-toggle";
import { ListSortMenu, type SortablePanelId } from "@/multi-pane/list-sort-menu";

/** The panel ids that carry a Sort-by gear (REQ-UI-110). */
const SORTABLE_PANEL_IDS: ReadonlySet<string> = new Set<SortablePanelId>([
  "platforms",
  "detections",
  "tracks",
  "alerts",
]);

/** Narrow an arbitrary active-panel id to a :type:`SortablePanelId`, or null. */
function asSortablePanelId(id: string | undefined): SortablePanelId | null {
  return id !== undefined && SORTABLE_PANEL_IDS.has(id) ? (id as SortablePanelId) : null;
}

/**
 * Render the per-group header controls (right-actions slot).
 *
 * @param props - dockview header-actions props; reads ``group.activePanel`` (for
 *     the sort-gear gating) + the group ``api`` (for the active-panel-change
 *     event) + forwards everything to each control.
 */
export function PanelHeaderActions(props: IDockviewHeaderActionsProps): React.ReactElement {
  const { api, group } = props;
  // Track the active panel id reactively: it can change as the operator
  // switches tabs within this group while the header component stays mounted.
  const [activePanelId, setActivePanelId] = useState<string | undefined>(
    () => group.activePanel?.id,
  );

  useEffect(() => {
    // Re-sync immediately (the group / active panel may have changed since the
    // last render) and on every active-panel change within the group.
    setActivePanelId(group.activePanel?.id);
    const disposable = api.onDidActivePanelChange(() => {
      setActivePanelId(group.activePanel?.id);
    });
    return () => {
      disposable.dispose();
    };
  }, [api, group]);

  const sortablePanelId = asSortablePanelId(activePanelId);

  return (
    <div className="flex h-full items-center" data-testid="panel-header-actions">
      {/* Sort-by gear — only for the three operator list panels (REQ-UI-110).
       * Left of the maximize toggle, per the window-control convention. */}
      {sortablePanelId !== null && <ListSortMenu panelId={sortablePanelId} />}
      {/* Every group. Right-most per window-control convention. */}
      <MaximizeToggle {...props} />
    </div>
  );
}
